import * as React from "react";
import { connect } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import * as _ from "lodash";
import Editor from "../../components/Editing/Editor";
import TextField from "../../components/Editing/TextField";
import { nameofFactory, hasWriteAccess } from "../../utils/utils";
import { driveVersionHasChildren } from "../../utils/pdApiUtils";
import { EditActions, editActions } from "../../actions/editActions";
import { getDomainOptionsByDomainId } from "../../utils/pdApiUtils";
import DropdownField from "../../components/Editing/DropdownField";
import NumberField from "../../components/Editing/NumberField";

type StateProps = {
  editedDriveVersion: Api.DriveVersion;
  storedDriveVersion: Api.DriveVersion;
  pdApi: State.PdApi;
  activeUser: Api.User | undefined;
};

type DispatchProps = {
  editActions: typeof editActions;
};

type OwnProps = {
  toggleModal: () => void;
};

const driveVersionFields = nameofFactory<Api.DriveVersion>();

export type EditDriveVersionProps = StateProps & DispatchProps & OwnProps;

export class EditDriveVersion extends React.Component<EditDriveVersionProps> {
  render(): JSX.Element {
    const { editedDriveVersion, storedDriveVersion, pdApi, activeUser, toggleModal } = this.props;
    const isNew = !storedDriveVersion;
    const { driveParameters } = pdApi;
    const deleteDisabled = storedDriveVersion && driveParameters && driveVersionHasChildren(storedDriveVersion.id, driveParameters);
    const domainOptions = getDomainOptionsByDomainId(editedDriveVersion.domain.id, false, pdApi);
    const permission = hasWriteAccess(activeUser, "Drive");

    return (
      <Editor
        itemName={"drive version"}
        originalObject={storedDriveVersion}
        editedObject={editedDriveVersion}
        hasWritePermission={permission}
        isValid={this.isAllFieldsValid()}
        cancel={this.props.editActions.cancelEditing}
        update={() => this.props.editActions.update("driveVersions")}
        create={() => this.props.editActions.add("driveVersions")}
        remove={{ callback: toggleModal 
          , disabled: deleteDisabled
          , disabledTooltip: "as it is used in one or more drive parameters."
        }}
      >
        <DropdownField
          id="domainName"
          label="Domain name"
          disabled={!isNew}
          value={editedDriveVersion.domain.id}
          options={domainOptions}
          search={true}
          onChange={id => {
            const domain = pdApi.domains[id];
            this.props.editActions.setEdited({ ...editedDriveVersion, domain });
          }}
        />
        <NumberField
          id="week"
          label="Week"
          readonly={!isNew}
          value={editedDriveVersion.week}
          isError={!this.isFieldValid(driveVersionFields("week"))}
          errorMessage={(editedDriveVersion.week < 1 || editedDriveVersion.week > 53) ? "The week must be between 1 and 53." : ""}
          onChange={week => this.props.editActions.setEdited({ ...editedDriveVersion, week })}
        />
        <NumberField
          id="year"
          label="Year"
          readonly={!isNew}
          value={editedDriveVersion.year}
          isError={!this.isFieldValid(driveVersionFields("year"))}
          errorMessage={(editedDriveVersion.year < 1951 || editedDriveVersion.year > 2099) ? "The year must be between 1951 and 2099." : ""}
          onChange={year => this.props.editActions.setEdited({ ...editedDriveVersion, year })}
        />
        {(!this.isFieldValid(driveVersionFields("domain"))) && <p className="errorMsgDecPadding">
                Week and Year combination must be unique in the domain.</p>}
        <TextField
          id="description"
          label="Description"
          value={editedDriveVersion.description}
          isError={!this.isFieldValid(driveVersionFields("description"))}
          errorMessage={!this.isEmptyString(editedDriveVersion.description) ? "The maximum number of characters must not exceed 128." : ""}
          onChange={description =>
            this.props.editActions.setEdited({ ...editedDriveVersion, description:description.trim() })
          }
          readonly={!permission}
        />
      </Editor>
    );
  }

  private isEmptyString = (str: string): boolean => !(!!str && str.length > 0);

  // Returns if domain, year and week combinations is unique
  private isUnique = (): boolean => {
    const { pdApi, editedDriveVersion } = this.props;
    const { week, year } = editedDriveVersion;

    if (!week || !year) {
      return true;
    } else {
      return _.values(pdApi.driveVersions).find(
        driveVersion =>
          driveVersion.id !== editedDriveVersion.id &&
          driveVersion.domain.id === editedDriveVersion.domain.id &&
          driveVersion.year === editedDriveVersion.year &&
          driveVersion.week === editedDriveVersion.week
      )
        ? false
        : true;
    }
  };
  // Returns if year is between 1951 and 2099
  private isYearValid = (): boolean => {
    const { editedDriveVersion } = this.props;
    const { year } = editedDriveVersion;
    return !!(year && year > 1950 && year < 2100);
  };
  // Returns if week is valid (between 1 and 53 according to Gregorian calendar)
  private isWeekValid = (): boolean => {
    const { editedDriveVersion } = this.props;
    const { week } = editedDriveVersion;
    return !!(week && week > 0 && week < 54);
  };

  private isFieldValid = (field: string): boolean => {
    const { editedDriveVersion } = this.props;
    const { description } = editedDriveVersion;
    switch (field) {
      case driveVersionFields("week"):
        return this.isWeekValid() && this.isUnique();
      case driveVersionFields("year"):
        return this.isYearValid() && this.isUnique();
      case driveVersionFields("domain"):
        return this.isUnique();
      case driveVersionFields("description"):
        return description.length <= 128;
      default:
        return true;
    }
  };

  private isAllFieldsValid = (): boolean => {
    const { editedDriveVersion } = this.props;
    const temp = _.keys(editedDriveVersion);
    for (const key of temp) {
      if (!this.isFieldValid(key)) {
        return false;
      }
    }
    return true;
  };
}

const mapStateToProps = (state: State.Root) => ({
  editedDriveVersion: state.editView.editedObject,
  storedDriveVersion: state.editView.storedObject,
  pdApi: state.pdApi,
  activeUser: state.main.activeUser
});

const mapDispatchToProps = (dispatch: Dispatch<EditActions>) => ({
  editActions: bindActionCreators({ ...editActions }, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(EditDriveVersion);


